/**
 * Каркас приложения: навигация, шапка, маршруты.
 *
 * Боковая панель отвечает на вопрос «где я и куда ещё можно», шапка — на
 * «с каким каналом я работаю и насколько свежие числа». Всё остальное живёт
 * на страницах: шапка, в которой лежат действия трёх разных экранов, —
 * это не шапка, а свалка.
 */

import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useIsFetching, useMutation, useQueryClient } from "@tanstack/react-query";
import { SessionProvider, useSession } from "./session";
import { api } from "./lib/api";
import { ago, dateTime } from "./lib/format";
import { Badge, LoadingDashboard } from "./components/ui";
import { TokelMark } from "./components/brand";
import {
  IconBook,
  IconBroadcast,
  IconChevronLeft,
  IconChevronRight,
  IconContent,
  IconFlask,
  IconLock,
  IconLogout,
  IconMegaphone,
  IconMenu,
  IconPulse,
  IconRefresh,
  IconSliders,
  IconSpark,
  IconTarget,
  IconUsers,
} from "./components/icons";
import AuthPage from "./pages/Auth";
import ResetPasswordPage from "./pages/ResetPassword";
import HealthPage from "./pages/Health";
import OpportunitiesPage from "./pages/Opportunities";
import AudiencePage from "./pages/Audience";
import ContentPage from "./pages/Content";
import AdsPage from "./pages/Ads";
import CompetitorsPage from "./pages/Competitors";
import ExperimentsPage from "./pages/Experiments";
import ChannelsPage from "./pages/Channels";
import SettingsPage from "./pages/Settings";
import MethodologyPage from "./pages/Methodology";

export default function App() {
  return (
    <SessionProvider>
      <Root />
    </SessionProvider>
  );
}

function Root() {
  const { me, loading } = useSession();

  if (loading) {
    return (
      <div className="content">
        <LoadingDashboard />
      </div>
    );
  }

  if (!me) {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<AuthPage />} />
      </Routes>
    );
  }

  return <Shell />;
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof IconPulse;
  pro?: boolean;
}

const NAV: { title: string; items: NavItem[] }[] = [
  {
    title: "Состояние",
    items: [{ to: "/health", label: "Здоровье канала", icon: IconPulse }],
  },
  {
    title: "Детальная аналитика",
    items: [
      { to: "/opportunities", label: "Возможности", icon: IconSpark, pro: true },
      { to: "/audience", label: "Аудитория", icon: IconUsers, pro: true },
      { to: "/content", label: "Контент", icon: IconContent, pro: true },
      { to: "/ads", label: "Закупы", icon: IconMegaphone, pro: true },
      { to: "/competitors", label: "Конкуренты", icon: IconTarget, pro: true },
      { to: "/experiments", label: "Эксперименты", icon: IconFlask, pro: true },
    ],
  },
  {
    title: "Настройка",
    items: [
      { to: "/channels", label: "Каналы", icon: IconBroadcast },
      { to: "/settings", label: "Настройки", icon: IconSliders },
      { to: "/methodology", label: "Как считаются числа", icon: IconBook },
    ],
  },
];

const COMPACT_KEY = "tca.sidebar.compact";

/**
 * Свёрнутость меню: по умолчанию её решает ширина экрана, но выбор человека
 * всегда сильнее.
 *
 * Одной вёрсткой это не делается. Сузить панель медиазапросом легко, а вот
 * спрятать в ней подписи тем же правилом — нет: класс `.compact` живёт в
 * разметке, и CSS не умеет его «дописать». Без этого узкая панель показывает
 * обрезки слов — «СОСТО», «НАСТР», — и выглядит поломкой.
 */
function useCompactSidebar(): [boolean, () => void] {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 1100px)").matches,
  );
  const [choice, setChoice] = useState<boolean | null>(() => {
    const stored = localStorage.getItem(COMPACT_KEY);
    return stored === null ? null : stored === "1";
  });

  useEffect(() => {
    const query = window.matchMedia("(max-width: 1100px)");
    const listen = (event: MediaQueryListEvent) => setNarrow(event.matches);
    query.addEventListener("change", listen);
    return () => query.removeEventListener("change", listen);
  }, []);

  const compact = choice ?? narrow;
  return [
    compact,
    () => {
      localStorage.setItem(COMPACT_KEY, compact ? "0" : "1");
      setChoice(!compact);
    },
  ];
}

function Shell() {
  const { me, isPro } = useSession();
  const [drawer, setDrawer] = useState(false);
  const [compact, toggleCompact] = useCompactSidebar();
  const location = useLocation();

  // Ящик закрывается при переходе сам: иначе на телефоне новая страница
  // открывается за шторкой, и её не видно.
  useEffect(() => setDrawer(false), [location.pathname]);

  const initials = (me?.user.full_name || me?.user.email || "?").trim().slice(0, 1).toUpperCase();

  return (
    <div className="shell">
      {drawer && <button className="scrim" aria-label="Закрыть меню" onClick={() => setDrawer(false)} />}

      <aside className={`sidebar ${compact ? "compact" : ""} ${drawer ? "open" : ""}`.trim()}>
        <div className="sidebar-head">
          <NavLink to="/health" className="brand-link">
            <TokelMark size={28} title="Tokel" />
            <span style={{ minWidth: 0 }}>
              <span className="brand-word">tokel</span>
              <span className="brand-sub">{me?.workspace?.name ?? "Аналитика каналов"}</span>
            </span>
          </NavLink>
          <button
            className="sidebar-toggle"
            onClick={toggleCompact}
            title={compact ? "Развернуть меню" : "Свернуть меню"}
            aria-label={compact ? "Развернуть меню" : "Свернуть меню"}
          >
            {compact ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
          </button>
        </div>

        <nav className="nav" aria-label="Разделы">
          {NAV.map((group) => (
            <div key={group.title} style={{ display: "contents" }}>
              <div className="nav-group-title">{group.title}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={compact ? item.label : undefined}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  <item.icon className="nav-icon" size={18} />
                  <span className="nav-label">{item.label}</span>
                  {item.pro && !isPro && <IconLock className="lock" size={13} />}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <SidebarFoot initials={initials} />
      </aside>

      <div className="main">
        <TopBar onOpenDrawer={() => setDrawer(true)} />
        <main className="content">
          <Routes>
            <Route path="/" element={<Navigate to="/health" replace />} />
            <Route path="/health" element={<HealthPage />} />
            <Route path="/opportunities" element={<OpportunitiesPage />} />
            <Route path="/audience" element={<AudiencePage />} />
            <Route path="/content" element={<ContentPage />} />
            <Route path="/ads" element={<AdsPage />} />
            <Route path="/competitors" element={<CompetitorsPage />} />
            <Route path="/experiments" element={<ExperimentsPage />} />
            <Route path="/channels" element={<ChannelsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="*" element={<Navigate to="/health" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function SidebarFoot({ initials }: { initials: string }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSuccess: () => {
      queryClient.clear();
      navigate("/");
      window.location.reload();
    },
  });

  return (
    <div className="sidebar-foot">
      <span className="avatar" aria-hidden="true">
        {initials}
      </span>
      <span className="who">
        <b>{me?.user.full_name || me?.user.email}</b>
        <span>{me?.workspace?.role_title ?? ""}</span>
      </span>
      <button
        className="ghost icon"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
        title="Выйти"
        aria-label="Выйти"
      >
        <IconLogout size={17} />
      </button>
    </div>
  );
}

/**
 * Шапка. Здесь только то, что относится ко ВСЕМУ приложению: за какой канал
 * смотрим, насколько свежие данные и в каком пространстве работаем.
 */
function TopBar({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  const { me, channels, channel, selectChannel, selectWorkspace, isPro } = useSession();
  const queryClient = useQueryClient();
  const fetching = useIsFetching();

  const initial = (channel?.title ?? "?").trim().slice(0, 1).toUpperCase();

  return (
    <header className="topbar">
      <button className="ghost icon burger" onClick={onOpenDrawer} aria-label="Меню">
        <IconMenu size={19} />
      </button>

      {channels.length > 0 ? (
        <div className="channel-picker">
          <span className="chan-avatar" aria-hidden="true">
            {initial}
          </span>
          <select
            value={channel?.id ?? ""}
            onChange={(event) => selectChannel(Number(event.target.value))}
            aria-label="Канал"
          >
            {channels.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <span className="muted">Каналов пока нет</span>
      )}

      {channel && (
        <span className="sync-state" title={`Последний сбор ленты: ${dateTime(channel.last_ingest_at)}`}>
          <span className={`status-dot ${channel.last_ingest_at ? "ok" : "unknown"}`} />
          данные: {ago(channel.last_ingest_at)}
        </span>
      )}

      <span className="spacer" />

      <button
        className="ghost icon"
        onClick={() => queryClient.invalidateQueries()}
        title="Перечитать данные"
        aria-label="Перечитать данные"
        disabled={fetching > 0}
      >
        <IconRefresh
          size={17}
          style={fetching > 0 ? { animation: "spin 900ms linear infinite" } : undefined}
        />
      </button>

      {(me?.workspaces.length ?? 0) > 1 && (
        <select
          value={me?.workspace?.id ?? ""}
          onChange={(event) => selectWorkspace(Number(event.target.value))}
          aria-label="Рабочее пространство"
          style={{ width: "auto", maxWidth: 200 }}
        >
          {me?.workspaces.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      )}

      <Badge tone={isPro ? "brand" : "muted"}>{isPro ? "Детальная аналитика" : "Бесплатный тариф"}</Badge>
    </header>
  );
}
